import { useState } from 'react';
import { useAi, useAiBacktest, useAiFeedback, useSubmitAiFeedback } from '@/lib/query';
import type { AiBacktestRow } from '@shared/domain';
import { fmt, ts } from '@/lib/format';
import { useAuth } from '@/state/auth';
import { Card, MetricCard, SectionTitle, Sparkline, Badge, EmptyHint } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';
import { Icon } from '@/components/layout/Icons';

const CAPS = [
  'DO 异常预测：连续 DO/pH + 离散氨氮/亚盐 混合时序',
  '智能投喂：规格 · 日龄 · 水温 · 水质',
  '科研：试验组/对照组 · 生长与补充水质',
  '报表：多参数相关 · 1–3 年追溯 / Excel 导出',
];

export default function AiResearch() {
  const { data } = useAi();
  const risk = data?.anomalyRiskPct ?? 0;
  const riskTone = risk > 60 ? 'alarm' : risk > 30 ? 'warn' : 'default';
  return (
    <div>
      <PageBanner />
      <div className="mb-6">
        <h2 className="text-[24px] font-bold text-slate-900">AI 与科研</h2>
        <p className="mt-1 text-[14px] text-slate-500">混合预测 · 投喂建议 · 试验与长周期分析</p>
        <div className="mt-1 text-[13px] text-slate-400">推理模型：<span className="font-mono">{data?.model ?? '—'}@{data?.modelVersion ?? '—'}</span> · 接口数据</div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <MetricCard label="DO 1h 预测" value={fmt(data?.doPredict1h, 2)} unit="mg/L" hint={`horizon ${data?.horizonMin ?? 60}min`} />
        <MetricCard label="建议单餐投喂" value={data?.feedSuggestKgPerMeal ?? '—'} unit="kg/餐" />
        <MetricCard label="置信度（校准）" value={`${data?.calibratedConfidencePct ?? data?.modelConfidencePct ?? '—'}%`} tone="brand" hint={data?.empiricalHitRatePct != null ? `实测命中率 ${data.empiricalHitRatePct}%` : `样本累积中（启发 ${data?.modelConfidencePct ?? '—'}%）`} />
        <MetricCard label="DO 异常风险" value={`${risk}%`} tone={riskTone as 'default' | 'warn' | 'alarm'} hint="跌破低带宽概率" />
        <MetricCard label="试验队列" value={<span className="text-[18px]">{data?.trialQueue ?? '—'}</span>} />
      </div>

      <Card className="mb-6 p-5">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[14px] font-semibold text-slate-700">DO 近端序列与 1h 预测</span>
          <span className="text-[12px] text-slate-400">来自时序历史（TimescaleDB / 环形缓冲）</span>
        </div>
        {data?.doSeries && data.doSeries.length > 1 ? (
          <div className="flex items-end gap-6">
            <Sparkline data={data.doSeries} width={360} height={56} stroke="#17a8ab" />
            <div className="text-[13px] text-slate-500">
              当前 → 预测 <b className="tabular-nums text-slate-800">{fmt(data.doPredict1h, 2)} mg/L</b>
            </div>
          </div>
        ) : (
          <div className="text-[13px] text-slate-400">数据采集中…</div>
        )}
      </Card>

      <BacktestPanel />

      <FeedbackPanel />

      <Card className="p-6">
        <SectionTitle title={<span className="flex items-center gap-2"><Icon name="ai" size={18} /> 能力说明</span>} />
        <div className="space-y-2">
          {CAPS.map((c) => (
            <div key={c} className="rounded-lg border border-hairline px-4 py-3 text-[14px] text-slate-600">· {c}</div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/** G1 — walk-forward backtest table: per-metric error (MAE/RMSE/MAPE) + drift. */
function BacktestPanel() {
  const { data, isLoading } = useAiBacktest();
  return (
    <Card className="mb-6 p-5">
      <SectionTitle
        title={<span className="flex items-center gap-2"><Icon name="ai" size={18} /> 模型可信度 · 回测误差</span>}
        desc="对历史 DO 序列做滚动前推（walk-forward）回测，量化预测误差与精度漂移；漂移比 = 近半段 MAE ÷ 前半段 MAE，>1.5 触发 AiForecastDrift 告警。"
      />
      {isLoading ? (
        <div className="py-4 text-[13px] text-slate-400">回测计算中…</div>
      ) : !data || data.length === 0 ? (
        <EmptyHint>历史样本不足以进行回测，采集一段时间后再查看。</EmptyHint>
      ) : (
        <div className="mt-1 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-hairline text-left text-slate-400">
                <th className="py-2 pr-4 font-medium">指标 / 单元</th>
                <th className="py-2 pr-4 font-medium">样本数</th>
                <th className="py-2 pr-4 font-medium">提前量</th>
                <th className="py-2 pr-4 font-medium">MAE</th>
                <th className="py-2 pr-4 font-medium">RMSE</th>
                <th className="py-2 pr-4 font-medium">MAPE</th>
                <th className="py-2 font-medium">精度漂移</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={r.key} className="border-b border-hairline/60 last:border-0">
                  <td className="py-2 pr-4">
                    <span className="font-medium text-slate-700">{r.unit}</span>
                    <span className="ml-2 tabular-nums text-slate-400">{r.key}</span>
                  </td>
                  <td className="py-2 pr-4 tabular-nums text-slate-500">{r.n}</td>
                  <td className="py-2 pr-4 tabular-nums text-slate-500">{r.n > 0 ? `${r.horizonMin}min` : '—'}</td>
                  <td className="py-2 pr-4 tabular-nums text-slate-800">{r.n > 0 ? fmt(r.mae, 2) : '—'}</td>
                  <td className="py-2 pr-4 tabular-nums text-slate-800">{r.n > 0 ? fmt(r.rmse, 2) : '—'}</td>
                  <td className="py-2 pr-4 tabular-nums text-slate-800">{r.mape == null ? '—' : `${fmt(r.mape, 1)}%`}</td>
                  <td className="py-2"><DriftBadge drift={r.driftRatio} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 text-[12px] text-slate-400">
            MAE/RMSE 单位随指标（DO 为 mg/L）；误差升高或漂移比 &gt;1.5 时应重新标定探头或复核采集链路。
          </div>
        </div>
      )}
    </Card>
  );
}

function DriftBadge({ drift }: { drift: AiBacktestRow['driftRatio'] }) {
  if (drift == null) return <span className="text-slate-400">样本不足</span>;
  const tone =
    drift > 1.5
      ? 'bg-alarm-bg text-alarm-fg border-alarm-border'
      : drift > 1.0
        ? 'bg-warn-bg text-warn-fg border-warn-border'
        : 'bg-ok-bg text-ok-fg border-ok-border';
  const label = drift > 1.5 ? '退化' : drift > 1.0 ? '略升' : '稳定';
  return <Badge className={tone}>×{fmt(drift, 2)} {label}</Badge>;
}

/** G4: human-in-the-loop — accept/reject the current AI suggestion + feedback ledger. */
function FeedbackPanel() {
  const { user } = useAuth();
  const canAct = ['supervisor', 'engineer', 'admin'].includes(user?.role ?? '');
  const { data: ai } = useAi();
  const { data: fb } = useAiFeedback();
  const submit = useSubmitAiFeedback();
  const [msg, setMsg] = useState<string | null>(null);

  const decide = (action: 'accept' | 'reject') => {
    if (!ai) return;
    submit.mutate(
      {
        action,
        model: ai.model ?? 'unknown',
        modelVersion: ai.modelVersion ?? 'unknown',
        suggestedFeedKg: ai.feedSuggestKgPerMeal,
        suggestedDo1h: ai.doPredict1h,
        anomalyRiskPct: ai.anomalyRiskPct ?? 0,
      },
      { onSuccess: () => setMsg(action === 'accept' ? '已采纳（已记录反馈）' : '已驳回（已记录反馈）') },
    );
  };

  const rows = fb ?? [];
  const accepts = rows.filter((f) => f.action === 'accept').length;
  return (
    <Card className="mb-6 p-5">
      <SectionTitle title="人在环 · 建议采纳/驳回（G4）" desc="对当前 AI 建议做出采纳/驳回并留痕，回流数据用于后续模型校准与采纳率统计。" />
      {canAct ? (
        <div className="flex items-center gap-3">
          <button onClick={() => decide('accept')} disabled={!ai || submit.isPending} className="btn btn-dark disabled:opacity-50">采纳建议</button>
          <button onClick={() => decide('reject')} disabled={!ai || submit.isPending} className="btn btn-ghost disabled:opacity-50">驳回</button>
          {msg ? <span className="text-[13px] text-slate-500">{msg}</span> : null}
        </div>
      ) : (
        <div className="text-[13px] text-slate-400">当前角色无采纳/驳回权限（需 production.manage）。</div>
      )}
      <div className="mt-4">
        <div className="mb-2 text-[12px] text-slate-400">最近反馈（{rows.length} 条 · 采纳率 {rows.length ? Math.round((accepts / rows.length) * 100) : 0}%）</div>
        {rows.length === 0 ? (
          <div className="text-[13px] text-slate-400">暂无反馈记录。</div>
        ) : (
          rows.slice(0, 6).map((f) => (
            <div key={f.id} className="flex items-center justify-between border-b border-hairline/60 py-1.5 text-[13px] last:border-0">
              <span className="flex items-center gap-2">
                {f.action === 'accept' ? <Badge className="bg-ok-bg text-ok-fg border-ok-border">采纳</Badge> : <Badge className="bg-warn-bg text-warn-fg border-warn-border">驳回</Badge>}
                <span className="text-slate-500">建议投喂 {f.suggestedFeedKg} kg/餐 · DO 预测 {f.suggestedDo1h}</span>
              </span>
              <span className="text-slate-400">{f.actorName} · {ts(f.ts, false)}</span>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}
